import { LOCAL_STORAGE_KEY } from "@/constants/localStorageKey";
import {
  CLOSE_PORT,
  GET_DATA,
  GET_DATA_RESPONSE,
  READY_STATE,
  UPDATE_STORE,
} from "@/constants/shareWorker";
import { isEqual } from "lodash";
import { gdSlsReportError } from "./gdSlsRum";
import { safeParse } from "./safeParse";
interface TabSharedOptions {
  /**
   * @description: 用于区分不同的共享存储
   */
  name?: string;
  localStorageKey?: string;
  /**自定义worker地址 */
  sharedWorkerHref?: string;
}

/**
 * @description: 多tab页共享数据存储，当一个tab页更新数据时，其他tab页可监听到数据变化
 * 基于 sharedWorker 或者 localStorage 实现，
 */
export class TabShared {
  mode: "sharedWorker" | "localStorage" = "sharedWorker";
  sharedWorker?: SharedWorker;
  localStorageKey?: string;
  donePromise: Promise<void>;

  constructor({
    name = "sharedStoreWorker",
    localStorageKey = LOCAL_STORAGE_KEY.APP_STORE,
    sharedWorkerHref,
  }: TabSharedOptions = {}) {
    // 判断兼容性
    if (typeof SharedWorker === "undefined") {
      this.mode = "localStorage";
      this.localStorageKey = localStorageKey;
    } else {
      // 创建sharedWorker
      this.sharedWorker = new SharedWorker(
        sharedWorkerHref || "/shareStoreWorker.js",
        {
          name: sharedWorkerHref || name,
        }
      );

      this.sharedWorker.onerror = (e) => {
        console.error("sharedWorker error", e);
        gdSlsReportError(e.error || new Error("sharedWorker error"));
      };

      this.sharedWorker.port.onmessageerror = (e) => {
        console.error("sharedWorker port onmessageerror", e);
        gdSlsReportError(
          new Error("sharedWorker port onmessageerror" + e.data)
        );
      };

      // 异步状态，等待sharedWorker初始化完成
      this.donePromise = new Promise((resolve) => {
        // 监听SharedWorker的消息事件
        this.sharedWorker.port.onmessage = (event) => {
          if (event.data?.type === READY_STATE) {
            resolve();
          }
        };
      });

      // 开启端口
      this.sharedWorker.port.start();

      // 页面关闭时，通知关闭端口
      window.addEventListener("beforeunload", () => {
        this.sharedWorker?.port.postMessage({ type: CLOSE_PORT });
      });
    }
  }
  /**
   * @description: 监听共享存储的数据变化
   * @param callback
   * @returns 取消监听的函数
   */
  onSyncData(callback: (data: any) => void) {
    if (this.mode === "sharedWorker") {
      const onMessage = (e: MessageEvent) => {
        if (e.data?.type === UPDATE_STORE) {
          callback(e.data.data);
        }
      };
      this.sharedWorker?.port.addEventListener("message", onMessage);

      return () => {
        this.sharedWorker?.port.removeEventListener("message", onMessage);
      };
    } else {
      const onStorage = (e: StorageEvent) => {
        if (e.key === this.localStorageKey) {
          // 回传更新的数据
          if (e.oldValue !== e.newValue) {
            const oldObject = safeParse(e.oldValue);
            const newObject = safeParse(e.newValue);

            // 取出更新的对象的数据
            const updateObject = Object.keys(newObject).reduce((acc, key) => {
              if (!isEqual(oldObject[key], newObject[key])) {
                acc[key] = newObject[key];
              }
              return acc;
            }, {} as any);

            callback(updateObject);
          }
        }
      };

      window.addEventListener("storage", onStorage);

      return () => {
        window.removeEventListener("storage", onStorage);
      };
    }
  }

  /**
   * @description: 向共享存储发送数据
   * @param data
   */
  async updateData(data: any) {
    await this.donePromise;

    if (this.mode === "sharedWorker") {
      this.sharedWorker?.port.postMessage({
        type: UPDATE_STORE,
        data,
      });
    } else {
      const old = safeParse(localStorage.getItem(this.localStorageKey));
      localStorage.setItem(
        this.localStorageKey,
        JSON.stringify({ ...old, ...data })
      );
    }
  }

  /**
   * @description: 获取共享存储的数据 异步的
   * @returns 共享的状态数据
   */
  getSharedStore() {
    if (this.mode === "sharedWorker") {
      return new Promise((resolve) => {
        this.donePromise.then(() => {
          const onMessage = (e: MessageEvent) => {
            if (e.data?.type === GET_DATA_RESPONSE) {
              resolve(e.data?.data);
            }
            this.sharedWorker?.port.removeEventListener("message", onMessage);
          };

          this.sharedWorker?.port.addEventListener("message", onMessage);
          this.sharedWorker?.port.postMessage({ type: GET_DATA, key: "store" });
        });

        // 超时处理，避免sharedWorker不响应，导致阻塞
        setTimeout(() => {
          resolve({});
        }, 300);
      });
    } else {
      return Promise.resolve(
        safeParse(localStorage.getItem(this.localStorageKey)) || {}
      );
    }
  }
}
